// Zustand store for live scan state.
// UI_SDD.md Section 10

import { create } from 'zustand'
import type {
  LlmUnavailableEvent,
  AgentEvent,
  AgentFailure,
  WaveFinishedEvent,
  AgentStatus,
  BudgetWarningEvent,
  CriticEvent,
  FindingEvent,
  RoundDecisionRequestedEvent,
  RoundDecisionResolvedEvent,
  ScanEvent,
  ScanState,
} from '../types/api'
import { AGENT_LABELS, AGENT_WAVE, WAVE_AGENTS } from '../types/api'
import type { AgentInputs, SavedAgentRow } from '../types/agents'

export interface AgentState {
  agent_id: string
  label: string
  status: AgentStatus
  wave: number
  findings_count: number
  tokens_used: number
  duration_seconds: number
  error: string | null    // set when status === 'failed'
  warning: string | null  // set when status === 'warned' (tool not available in repo)
  skip_reason: string | null
  /** DEC-094: this agent's Tier-2 pause landed and is awaiting Approve/Decline.
   * Per-agent — distinct from the scan-wide llmUnavailable pause above. */
  roundDecisionPending: boolean
  /** DEC-187: this agent's findings by severity. Live: raw counts from
   * finding_produced events. Saved: after review, from /agents. */
  severity: SeverityCounts
  /** DEC-188: what the agent received. undefined = not received yet; null = not
   * recorded (a scan before migration 034, or an event without the field). */
  inputs?: AgentInputs | null
  /** DEC-197: the recorded failure (live: without `detail`; saved: with it). */
  failure?: AgentFailure | null
  /** DEC-197: every warning or reported problem, not only the first. */
  warnings?: string[]
  /** DEC-195: false when a saved scan did not record this agent's time and tokens. */
  timeRecorded?: boolean
}

export interface SeverityCounts {
  critical: number
  high: number
  medium: number
  low: number
}

const EMPTY_SEVERITY: SeverityCounts = { critical: 0, high: 0, medium: 0, low: 0 }

/** Severity counts from an untrusted map: unknown keys ignored, absence is zeros. */
function toSeverity(counts: Record<string, number> | null | undefined): SeverityCounts {
  const out = { ...EMPTY_SEVERITY }
  for (const [key, n] of Object.entries(counts ?? {})) {
    const sev = key.toLowerCase() as keyof SeverityCounts
    if (sev in out && typeof n === 'number') out[sev] += n
  }
  return out
}

const TERMINAL_AGENT: ReadonlySet<string> = new Set(['complete', 'warned', 'failed', 'skipped', 'done_with_problems'])

interface ScanStore {
  scanId: string | null
  scanState: ScanState | null
  agents: Record<string, AgentState>
  liveFindings: FindingEvent[]     // last 20, newest first
  criticSummary: CriticEvent | null
  budgetWarning: BudgetWarningEvent | null
  /** DEC-067: set when the scan lost LLM access; drives the decision modal. */
  llmUnavailable: LlmUnavailableEvent | null
  clearLlmUnavailable: () => void
  progress: number                  // 0–100
  streamComplete: boolean
  // Cumulative severity counts (Redesign v3 segmented bar, UI_SDD §R.3).
  // Accurate because the SSE stream replays from event 0 on (re)connect.
  severityCounts: SeverityCounts
  /** True once any agent_status_changed event arrived on this visit. */
  agentEventsSeen: boolean
  /** DEC-185: the agent rows came from saved data (GET /scans/{id}/agents), not the
   * live stream. Counts are after review; per-agent time and tokens were not recorded. */
  fromSaved: boolean
  /** DEC-188: D9 orphan modules that lowered cost. Saved scans only; null live. */
  deadFiles: number | null
  /** DEC-195: each wave's run time, from wave_finished events or saved rows. */
  waveRunSeconds: Record<number, number | null>
  /** DEC-195: when each wave's first agent started, from the event timestamps, so a
   *  running wave's time can tick. */
  waveStartedAt: Record<number, string>

  // Actions
  initScan: (scanId: string) => void
  handleEvent: (event: ScanEvent) => void
  /** DEC-185: load a finished scan's saved agent outcomes. Ignored once live agent
   * events have arrived, because those are the better record. */
  hydrateFromSaved: (rows: SavedAgentRow[], deadFiles?: number | null) => void
  /** DEC-185: severity counts of the saved (post-Critic) findings. */
  setSavedSeverity: (counts: Record<string, number>) => void
  /** A finished scan's bar shows the Critic's reviewed counts, live events or not. */
  setReviewedSeverity: (counts: Record<string, number>) => void
  reset: () => void
}

function buildInitialAgents(): Record<string, AgentState> {
  const agents: Record<string, AgentState> = {}
  for (const [waveStr, ids] of Object.entries(WAVE_AGENTS)) {
    const wave = Number(waveStr)
    for (const id of ids) {
      agents[id] = {
        agent_id: id,
        label: AGENT_LABELS[id] ?? id,
        status: 'waiting',
        wave,
        findings_count: 0,
        tokens_used: 0,
        duration_seconds: 0,
        error: null,
        warning: null,
        skip_reason: null,
        roundDecisionPending: false,
        severity: { ...EMPTY_SEVERITY },
        failure: null,
        warnings: [],
        timeRecorded: true,
      }
    }
  }
  return agents
}

/** A saved /agents row as an agent (DEC-185), also used by the What happened pop-up. */
export function savedRowToAgent(row: SavedAgentRow): AgentState {
  return {
    agent_id: row.agent_id,
    label: row.label || AGENT_LABELS[row.agent_id] || row.agent_id,
    // `not_run` has no live equivalent. It stays 'waiting' and `fromSaved` makes the
    // views read it as "Not run", so no live status means two things.
    status: row.status === 'not_run' ? 'waiting' : row.status,
    wave: AGENT_WAVE[row.agent_id] ?? row.wave,
    findings_count: row.findings_after_review,
    // DEC-195: recorded on newer scans; otherwise the views print "not recorded".
    tokens_used: row.not_recorded ? 0 : row.tokens_used ?? 0,
    duration_seconds: row.not_recorded ? 0 : row.duration_seconds ?? 0,
    timeRecorded: !row.not_recorded && row.duration_seconds != null,
    failure: row.failure ?? null,
    warnings:
      row.warnings ??
      ((row.status === 'warned' || row.status === 'done_with_problems') && row.reason ? [row.reason] : []),
    error: row.status === 'failed' ? row.reason : null,
    warning: row.status === 'warned' || row.status === 'done_with_problems' ? row.reason : null,
    skip_reason: row.status === 'skipped' ? row.reason : null,
    roundDecisionPending: false,
    // Tolerate a server without DEC-188: no breakdown reads as zeros, and the views
    // fall back to a neutral line when findings exist without one.
    severity: toSeverity(row.findings_by_severity as unknown as Record<string, number> | undefined),
    inputs: row.inputs ?? null,
  }
}

function computeProgress(agents: Record<string, AgentState>): number {
  const all = Object.values(agents).filter(
    (a) => a.status !== 'skipped' && a.status !== 'waiting',
  )
  const total = Object.values(agents).filter((a) => a.status !== 'skipped').length
  if (total === 0) return 0
  const done = all.filter(
    (a) => TERMINAL_AGENT.has(a.status) && a.status !== 'skipped',
  ).length
  return Math.round((done / total) * 100)
}

export const useScanStore = create<ScanStore>((set) => ({
  scanId: null,
  scanState: null,
  agents: buildInitialAgents(),
  liveFindings: [],
  criticSummary: null,
  budgetWarning: null,
  llmUnavailable: null,
  progress: 0,
  streamComplete: false,
  severityCounts: { ...EMPTY_SEVERITY },
  agentEventsSeen: false,
  fromSaved: false,
  deadFiles: null,
  waveRunSeconds: {},
  waveStartedAt: {},

  initScan: (scanId) =>
    set({
      scanId,
      scanState: 'queued',
      agents: buildInitialAgents(),
      liveFindings: [],
      criticSummary: null,
      budgetWarning: null,
      llmUnavailable: null,
      progress: 0,
      streamComplete: false,
      severityCounts: { ...EMPTY_SEVERITY },
      agentEventsSeen: false,
      fromSaved: false,
      deadFiles: null,
      waveRunSeconds: {},
      waveStartedAt: {},
    }),

  clearLlmUnavailable: () => set({ llmUnavailable: null }),

  handleEvent: (event) => {
    set((state) => {
      switch (event.event_type) {
        case 'scan_state_changed': {
          const newState = { ...state, scanState: event.state }
          if (['complete', 'partial', 'failed'].includes(event.state)) {
            return { ...newState, progress: 100, streamComplete: true }
          }
          return newState
        }

        case 'agent_status_changed': {
          const e = event as AgentEvent & { inputs?: AgentInputs | null }
          const wave = AGENT_WAVE[e.agent_id] ?? e.wave
          const previous = state.agents[e.agent_id]
          // DEC-188: `inputs` rides on the terminal event. Present (even null) wins;
          // absent on a terminal event means the backend did not record it (null);
          // absent on a non-terminal event keeps whatever was known.
          const inputs =
            'inputs' in e
              ? (e.inputs ?? null)
              : TERMINAL_AGENT.has(e.status)
                ? (previous?.inputs ?? null)
                : previous?.inputs
          const updated: AgentState = {
            agent_id: e.agent_id,
            label: e.agent_label,
            status: e.status,
            wave,
            findings_count: e.findings_count,
            tokens_used: e.tokens_used,
            duration_seconds: e.duration_seconds,
            error: e.error,
            warning: e.warning ?? null,
            // DEC-146: this field has existed since the redesign and was hardcoded to
            // null because the backend never emitted a skipped agent. It does now.
            skip_reason: e.reason ?? null,
            roundDecisionPending: false,  // any full status update means the pause (if any) is over
            // Live counts accumulate from finding_produced; a status change never resets them.
            severity: previous?.severity ?? { ...EMPTY_SEVERITY },
            inputs,
            failure: e.failure ?? previous?.failure ?? null,
            warnings: e.warnings ?? (e.warning ? [e.warning] : previous?.warnings ?? []),
            timeRecorded: true,
          }
          const agents = { ...state.agents, [e.agent_id]: updated }
          const waveStartedAt =
            e.status === 'running' && !state.waveStartedAt[wave] && e.timestamp
              ? { ...state.waveStartedAt, [wave]: e.timestamp }
              : state.waveStartedAt
          return { ...state, agents, waveStartedAt, progress: computeProgress(agents), agentEventsSeen: true }
        }

        case 'wave_finished': {
          const e = event as WaveFinishedEvent
          return { ...state, waveRunSeconds: { ...state.waveRunSeconds, [e.wave]: e.run_seconds ?? null } }
        }

        case 'finding_produced': {
          const e = event as FindingEvent
          const liveFindings = [e, ...state.liveFindings].slice(0, 20)
          const sev = (e.severity ?? '').toLowerCase() as keyof SeverityCounts
          const severityCounts =
            sev in state.severityCounts
              ? { ...state.severityCounts, [sev]: state.severityCounts[sev] + 1 }
              : state.severityCounts
          // DEC-187: the same count, attributed to the agent, drives its wave's line.
          const owner = state.agents[e.agent_id]
          const agents =
            owner && sev in owner.severity
              ? {
                  ...state.agents,
                  [e.agent_id]: { ...owner, severity: { ...owner.severity, [sev]: owner.severity[sev] + 1 } },
                }
              : state.agents
          return { ...state, liveFindings, severityCounts, agents }
        }

        case 'critic_summary': {
          return {
            ...state,
            criticSummary: event as CriticEvent,
            progress: 95,
          }
        }

        case 'budget_warning': {
          return {
            ...state,
            budgetWarning: event as BudgetWarningEvent,
          }
        }

        case 'llm_unavailable': {
          return {
            ...state,
            llmUnavailable: event as LlmUnavailableEvent,
          }
        }

        case 'round_decision_requested': {
          const e = event as RoundDecisionRequestedEvent
          const existing = state.agents[e.agent_id]
          if (!existing) return state
          const agents = { ...state.agents, [e.agent_id]: { ...existing, roundDecisionPending: true } }
          return { ...state, agents }
        }

        case 'round_decision_resolved': {
          const e = event as RoundDecisionResolvedEvent
          const existing = state.agents[e.agent_id]
          if (!existing) return state
          const agents = { ...state.agents, [e.agent_id]: { ...existing, roundDecisionPending: false } }
          return { ...state, agents }
        }

        default:
          return state
      }
    })
  },

  hydrateFromSaved: (rows, deadFiles = null) =>
    set((state) => {
      if (state.agentEventsSeen) return state
      const agents = buildInitialAgents()
      const waveRunSeconds: Record<number, number | null> = {}
      for (const row of rows) {
        agents[row.agent_id] = savedRowToAgent(row)
        if (typeof row.wave_run_seconds === 'number') waveRunSeconds[AGENT_WAVE[row.agent_id] ?? row.wave] = row.wave_run_seconds
      }
      return {
        ...state,
        agents,
        waveRunSeconds,
        fromSaved: true,
        progress: 100,
        streamComplete: true,
        deadFiles: typeof deadFiles === 'number' ? deadFiles : null,
      }
    }),

  setSavedSeverity: (counts) =>
    set((state) => {
      if (state.agentEventsSeen) return state
      return { ...state, severityCounts: toSeverity(counts) }
    }),

  setReviewedSeverity: (counts) => set((state) => ({ ...state, severityCounts: toSeverity(counts) })),

  reset: () =>
    set({
      scanId: null,
      scanState: null,
      agents: buildInitialAgents(),
      liveFindings: [],
      criticSummary: null,
      budgetWarning: null,
      llmUnavailable: null,
      progress: 0,
      streamComplete: false,
      severityCounts: { ...EMPTY_SEVERITY },
      agentEventsSeen: false,
      fromSaved: false,
      deadFiles: null,
      waveRunSeconds: {},
      waveStartedAt: {},
    }),
}))
